import { ReactNode } from 'react';

import * as styles from '../FirstResponder.module.css';
import ConsoleHeader from '../Presentation/ConsoleHeader';
import DiagnosticsSection from '../Presentation/DiagnosticsSection';
import HeadlineStats from '../Presentation/HeadlineStats';
import NeedsAttentionCard from '../Presentation/NeedsAttentionCard';
import PilotSettingsSection from '../Presentation/PilotSettingsSection';
import ResultsSection from '../Presentation/ResultsSection';
import JobsCard from './JobsCard';
import JobModal from './JobModal';
import ModeReasonModal, { ModeReasonTarget } from './ModeReasonModal';
import {
  BreachTakeover,
  ErrorFrame,
  LoadingFrame,
  SafetyUnknownTakeover,
  UnauthorizedFrame,
  UnauthorizedReason,
} from './StateShell';
import StopConfirmModal from './StopConfirmModal';
import {
  LiveAuditRow,
  LiveConsoleData,
  LiveJobRow,
  LivePage,
  LivePilotPolicy,
  LiveSettingsDraft,
  LiveStopCopy,
  LiveWorkerRow,
  runScopeFor,
} from './liveViewModel';
import { useCountdown } from './useCountdown';

/*
 * LIVE console — pure presentation over the view model plus server-shaped
 * pages. This component owns NO collection: jobs, workers and audit rows all
 * arrive as adapter-produced pages (canonical wire pagination mapped to
 * frontend casing in the adapter), and every page change is a callback to the
 * data owner. It also owns no business values — headings, closed vocabulary
 * and layout only.
 *
 * Safety is a closed union, and it is a BANNER over the data, never a takeover
 * of it (GRAVY-10373 Task 8 — the console exists to show what the pilot is
 * doing, and a safety failure is when that matters most):
 *   clear    -> console, no banner.
 *   unknown  -> "Safety status unavailable" banner ABOVE the same console.
 *   breached -> STOP banner with reasons ABOVE the same console.
 *
 * Under `unknown` and `breached` the operator still reads everything —
 * watching, jobs, attention, results, the settings audit and diagnostics. Only
 * CONFIGURATION is locked: Edit pilot settings, Save draft, Activate and Go
 * Live. Stop is never removed by a safety failure — see the
 * control-availability comment in the component body.
 *
 * GRAVY-10373 convergence, Phase 1: the header/stats/attention/results/settings/diagnostics
 * sections below were extracted into ../Presentation/* so the real connected page (Phase 2+) can
 * render the exact same components fed by real-data adapters instead of this file's fixture-
 * derived props. This file still owns the shell (safety-state switching, countdown, job/stop/
 * settings-editor modal wiring) and still supplies every prop from the fixture-derived view model
 * — nothing about its own behavior changes in this phase.
 */

export type OpenJob = { id: string; section: 'overview' | 'workers' } | null;

// GRAVY-10373 convergence, Phase 2: no longer carries onSaveDraft/onActivate/serverFloor — the
// specific editor implementation (SettingsEditor for the preview, ConnectedPolicyEditor for real
// data) is entirely the caller's concern, supplied via Props.settingsEditorSlot below. This is
// what lets both the preview and the real connected page render this exact same shell.
export type SettingsProps = {
  // null while no pilot configuration has been provisioned yet (a fresh environment). The console
  // must render that state, not crash on it: production met it on 2026-09-21 (Sentry TENANT-ADMIN-PROD-8T).
  active: LivePilotPolicy | null;
  draft: LiveSettingsDraft | null;
  auditPage: LivePage<LiveAuditRow>;
  editorOpen: boolean;
  onOpenEditor: () => void;
  onCloseEditor: () => void;
  onAuditPageChange: (page: number) => void;
  // GRAVY-10373 F8: a failed audit page fetch, rendered as an inline retry notice beside the audit
  // table. Optional — the preview's audit page never fails; only the real container supplies it.
  auditError?: { message: string; onRetry: () => void } | null;
};

type Props = {
  data: LiveConsoleData;
  jobsPage: LivePage<LiveJobRow>;
  selectedFilter: string;
  onSelectFilter: (key: string) => void;
  onJobsPageChange: (page: number) => void;
  openJob: OpenJob;
  // GRAVY-10373 Task 7: candidatesTotal rides alongside the paginated page (mirrors the wire's own
  // WirePage<WireWorkerRow> & { candidates_total } composition) — JobModal renders it.
  workersPage:
    | (LivePage<LiveWorkerRow> & { candidatesTotal: number | null })
    | null;
  onWorkersPageChange: (page: number) => void;
  // GRAVY-10373 M1: a failed getJob/getWorkers used to be a silent dead click. Optional — the
  // preview never supplies this (its fixture data never fails); the real connected container
  // does. Rendered as a small inline retry notice in the jobs card, never a blank modal.
  jobsLoadError?: { message: string; onRetry: () => void } | null;
  // GRAVY-10373 F8: a failed JOBS PAGE fetch (filter/pagination), as distinct from jobsLoadError's
  // failed job OPEN above. Kept separate because the two retries do different things — re-request
  // this page of the table vs. re-open this job — and one Retry that might mean either is no
  // better than the silent failure it replaces. Optional; the preview never fails.
  jobsPageError?: { message: string; onRetry: () => void } | null;
  // GRAVY-10373 F9: WHY the unauthorized frame is showing — a dead session (401) or a role that
  // may not view this console (403). Carried alongside `data` rather than inside its 'unauthorized'
  // variant so the view-model union (liveViewModel.ts) is untouched. Read only while
  // data.status === 'unauthorized'; the real container always states the reason explicitly.
  //
  // M9 (independent review): the default is 'session' — the 401 whose token refresh gave up is
  // the failure a caller that says nothing has almost certainly hit, and it is the only one of
  // the two whose fix the reader can carry out themselves. Defaulting to 'role' pointed the
  // common case at someone else's grant.
  unauthorizedReason?: UnauthorizedReason;
  // GRAVY-10373 CRITICAL fix: caller-owned, independent of `vm` — Stop (and its confirm modal)
  // must be reachable even in the 'loading'/'error'/'unauthorized' states, which carry no vm and
  // therefore no vm.stopConfirm. Always used for the confirm modal's copy (never vm.stopConfirm),
  // so there is exactly one source regardless of data status.
  stopConfirmCopy: LiveStopCopy;
  stopOpen: boolean;
  settings: SettingsProps;
  // The caller-constructed editor element (e.g. <SettingsEditor .../> or
  // <ConnectedPolicyEditor .../>), rendered only while settings.editorOpen && a view model exists.
  // null when the caller has nothing to show (e.g. not yet loaded).
  settingsEditorSlot: ReactNode;
  // Optional container-level transient alert (e.g. a stale-confirmation or failed-mode-transition
  // banner) rendered between the header and the main content. null/undefined renders nothing —
  // the preview never uses this; the real connected container does (Phase 4/5).
  bannerSlot?: ReactNode;
  onSelectJob: (id: string | null, section?: 'overview' | 'workers') => void;
  // GRAVY-10373 Task 3: real three-state mode control. The caller (Connected/index.tsx in
  // production; LivePreview/index.tsx's fixture cycle in the preview) decides what a click on
  // Start Shadow / Go Live / Back to Shadow means; this component only ever forwards the target
  // the header button legally offered — it computes no transition policy of its own.
  onRequestModeTransition: (target: ModeReasonTarget) => void;
  // Which reason-collecting modal is open (or none). Every mode transition other than Stop
  // collects a reason through the SAME ModeReasonModal — see that component's own doc comment for
  // why this replaced a separate GoLiveConfirmModal.
  modeReasonTarget: ModeReasonTarget | null;
  // The active pilot's client list (policyWire.active.clients in production) — shown, with its
  // count, only in the `live` variant of the modal.
  cohortClients: { customerId: number; name: string }[];
  onConfirmModeReason: (reason: string) => void;
  onCancelModeReason: () => void;
  // A mode POST (including Stop) is in flight. Never optimistic: the caller only flips displayed
  // state after a backend-confirmed refetch, and this flag exists purely to prevent a
  // double-submit while that round trip is outstanding.
  modePending: boolean;
  onRequestStop: () => void;
  onConfirmStop: (reason: string) => void;
  onCancelStop: () => void;
  onRetry: () => void;
  // F11: "Run one tick now" (Presentation/DiagnosticsSection.tsx) — caller-owned, same pattern as
  // every other mutation here (Connected/index.tsx owns the real request for production;
  // LivePreview/index.tsx supplies a no-op stub so the button still renders in the preview).
  onRunTick: () => void;
  runTickPending: boolean;
  runTickResult: string | null;
  // GRAVY-10816 bug 2: whether the signed-in admin may OPERATE the console (mode changes, Stop,
  // pilot settings). Connected/index.tsx derives it from the admin's user.roleId (TenantAdmin
  // only, the same field PrivateRoute keys on); the preview has no admin and leaves the default.
  // False renders the same page read-only: no mode/Stop buttons, Edit pilot settings disabled
  // with a role reason, editor slot withheld. The backend refuses those writes for the role anyway.
  canOperate?: boolean;
};

export const READ_ONLY_EDIT_REASON =
  'Read-only for your role. Pilot settings are changed by a Tenant Admin.';
// GRAVY-10816 (prod, 2026-09-21): a fresh environment has no pilot configuration until the
// provisioning task runs. The editor is based on the active policy, so it cannot open before one exists.
export const NO_CONFIGURATION_EDIT_REASON =
  'No pilot configuration yet. The first one is created by the provisioning task, not from this page.';
export const NO_CONFIGURATION_START_REASON =
  'Start Shadow needs a pilot configuration. The first one is created by the provisioning task.';

const LiveConsole = ({
  data,
  jobsPage,
  selectedFilter,
  onSelectFilter,
  onJobsPageChange,
  openJob,
  workersPage,
  onWorkersPageChange,
  jobsLoadError,
  jobsPageError,
  unauthorizedReason = 'session',
  stopConfirmCopy,
  stopOpen,
  settings,
  settingsEditorSlot,
  bannerSlot,
  onSelectJob,
  onRequestModeTransition,
  modeReasonTarget,
  cohortClients,
  onConfirmModeReason,
  onCancelModeReason,
  modePending,
  onRequestStop,
  onConfirmStop,
  onCancelStop,
  onRetry,
  onRunTick,
  runTickPending,
  runTickResult,
  canOperate = true,
}: Props) => {
  const ready = data.status === 'ready';
  const vm = ready ? data.vm : null;
  // The countdown ticker only makes sense while something is actually running — a gate-halted
  // mode (effectiveMode 'off') has nothing left to count down to, even if a mode is still armed.
  // Task 8: an UNKNOWN safety verdict does not halt anything — a LIVE pilot with no persisted
  // proof yet is still about to tick, and freezing the clock would misreport that. A BREACH does
  // halt it, so the clock stops there.
  const countdownEnabled =
    !!vm && vm.effectiveMode !== 'off' && vm.safety.state !== 'breached';
  const elapsed = useCountdown(countdownEnabled);

  /*
   * CONTROL AVAILABILITY IS SEMANTIC, not a single "is safety ok" boolean.
   *
   * GRAVY-10373 C1: Stop follows REQUESTED MODE alone — what is ARMED, not merely what the
   * guards are currently permitting. A gate-halted LIVE (requestedMode 'live', effectiveMode
   * 'off') is still configured to run and the backend still considers it not-off for
   * already_in_mode purposes — Stop must stay enabled so the operator can actually turn it off.
   * If something is armed it can be stopped — including, and especially, when safety is unknown
   * or breached. At OFF (both requested and effective) it is disabled because there is nothing
   * to stop. A safety failure must never remove this, the SAFER action (the GRAVY-10373 defect
   * this comment used to describe).
   *
   * CRITICAL fix: this reasoning applies with EQUAL force to the 'error' status (no vm at all,
   * e.g. after a failed reload from a stale-confirmation state) — an operator who can't currently
   * read the console's state must still be able to try to stop it; the backend is the authority
   * and answers already_in_mode if it's already off (surfaced via bannerSlot, same as any other
   * mode-transition failure). 'loading' and 'unauthorized' are the only genuinely inert states —
   * nothing is known yet, or the operator isn't allowed to act at all.
   *
   * GRAVY-10373 Task 3: the mode buttons themselves (Start Shadow / Go Live / Back to Shadow) are
   * now ConsoleHeader's own concern — each REQUESTED mode renders exactly its own legal button,
   * and only Go Live (escalating) is additionally gated on safety+pending. See ConsoleHeader's
   * doc comment.
   */
  const stopEnabled = vm ? vm.requestedMode !== 'off' : data.status === 'error';

  // Everything that edits configuration stays gated on proven safety — and, since Task 8, this is
  // the ONLY thing a safety failure takes away. It disables the Edit button, and it withholds the
  // caller's editor slot entirely so an editor that was already open cannot outlive the flip.
  //
  // 2026-09-08: editing is ALSO locked while First Responder is live. The API accepts a policy
  // save in every mode (SC-API-J10), but superseding the active policy under a live tick is the
  // documented never-do (AGENTS §7b(i)); the console enforces it: switch back to Shadow first.
  // The safety reason wins when both apply, because it is the more fundamental one.
  const liveLocksEditing = !!vm && vm.requestedMode === 'live';
  // GRAVY-10816 bug 2: a read-only role is the most fundamental lock of all, ahead of safety.
  const noConfiguration = settings.active === null;
  const editingDisabled =
    !canOperate ||
    noConfiguration ||
    !vm ||
    vm.safety.state !== 'clear' ||
    liveLocksEditing;
  const editingDisabledReason = !canOperate
    ? READ_ONLY_EDIT_REASON
    : noConfiguration
      ? NO_CONFIGURATION_EDIT_REASON
      : !vm || vm.safety.state !== 'clear'
        ? 'Editing is disabled until the safety status is clear.'
        : 'Editing is disabled while First Responder is live. Switch back to Shadow to change settings.';

  const detail = vm && openJob ? (vm.detailsById[openJob.id] ?? null) : null;

  return (
    <div className={styles.page} data-testid="flp-page">
      <ConsoleHeader
        vm={vm}
        stopDisabled={!stopEnabled}
        modePending={modePending}
        onRequestModeTransition={onRequestModeTransition}
        onStopClick={onRequestStop}
        readOnly={!canOperate}
        startShadowBlockedReason={
          noConfiguration ? NO_CONFIGURATION_START_REASON : null
        }
      />

      {bannerSlot}

      <div className={styles.content}>
        {data.status === 'loading' && <LoadingFrame />}
        {data.status === 'error' && (
          <ErrorFrame message={data.message} onRetry={onRetry} />
        )}
        {data.status === 'unauthorized' && (
          <UnauthorizedFrame reason={unauthorizedReason} />
        )}

        {/* Task 8: a banner ABOVE the console, never instead of it. */}
        {vm && vm.safety.state === 'breached' && (
          <BreachTakeover reasons={vm.safety.reasons} />
        )}
        {vm && vm.safety.state === 'unknown' && (
          <SafetyUnknownTakeover reason={vm.safety.reason} />
        )}

        {vm && (
          <>
            {/* LEVEL 1 — headline business metrics */}
            <HeadlineStats
              stats={vm.stats}
              activePolicy={settings.active}
              runScope={runScopeFor(vm.requestedMode)}
            />

            {/* NEEDS ATTENTION — derived from the unresolved collection */}
            <NeedsAttentionCard
              attention={vm.attention}
              onSelectJob={onSelectJob}
            />

            {/* LEVEL 2 — jobs (server-shaped page; JobsCard never slices) */}
            <JobsCard
              page={jobsPage}
              onPageChange={onJobsPageChange}
              filters={vm.filters}
              selectedFilter={selectedFilter}
              onSelectFilter={onSelectFilter}
              onSelectJob={onSelectJob}
              elapsed={elapsed}
              jobsLoading={data.status === 'ready' && data.jobsLoading}
              loadError={jobsLoadError ?? null}
              pageError={jobsPageError ?? null}
            />

            {/* RESULTS — cumulative since LIVE enablement */}
            <ResultsSection
              results={vm.results}
              runScope={runScopeFor(vm.requestedMode)}
            />

            {/* PILOT SETTINGS — derived display + audited edit flow */}
            <PilotSettingsSection
              active={settings.active}
              draft={settings.draft}
              auditPage={settings.auditPage}
              onAuditPageChange={settings.onAuditPageChange}
              auditError={settings.auditError ?? null}
              controlsDisabled={editingDisabled}
              controlsDisabledReason={editingDisabledReason}
              onOpenEditor={settings.onOpenEditor}
            />

            {/* DIAGNOSTICS — collapsed; never a leading safety card */}
            <DiagnosticsSection
              diagnostics={vm.diagnostics}
              onRunTick={onRunTick}
              runTickPending={runTickPending}
              runTickResult={runTickResult}
            />
          </>
        )}
      </div>

      {detail && openJob && workersPage && (
        <JobModal
          key={detail.id}
          detail={detail}
          workersPage={workersPage}
          onWorkersPageChange={onWorkersPageChange}
          ranking={vm!.ranking}
          elapsed={elapsed}
          initialSection={openJob.section}
          onClose={() => onSelectJob(null)}
        />
      )}
      {/* CRITICAL fix: renders whenever Stop is reachable at all — vm present (ready) OR the
          'error' status — never gated on vm alone, or the confirm modal (and therefore the
          ability to actually stop) would be unreachable in exactly the state that most needs it. */}
      {(vm || data.status === 'error') && stopOpen && (
        <StopConfirmModal
          copy={stopConfirmCopy}
          pending={modePending}
          onConfirm={onConfirmStop}
          onCancel={onCancelStop}
        />
      )}
      {vm && modeReasonTarget && (
        <ModeReasonModal
          target={modeReasonTarget}
          cohortClients={cohortClients}
          safety={vm.safety}
          pending={modePending}
          onConfirm={onConfirmModeReason}
          onCancel={onCancelModeReason}
        />
      )}
      {vm && settings.editorOpen && !editingDisabled && settingsEditorSlot}
    </div>
  );
};

export default LiveConsole;
